/**
 * Resolve a locally measured DRAFT profile into the same shape the pipeline
 * takes for a committed one.
 *
 * A draft differs from an override in the one way that matters here: an
 * override amends a committed profile and merges onto it, while a draft has no
 * committed profile at all and merges straight onto the base. So this cannot
 * reuse `resolveEffectiveProfile`'s two-layer path, and deliberately does not
 * try — a draft is never overridable, and pretending otherwise would invite an
 * override written against policy nobody approved.
 *
 * What it does share is the policy identity: the same canonical serialization
 * and the same FNV-1a hash, so a draft run's `policyHash` is comparable with
 * every other run's and the analysis cache keys on it correctly. Two drafts
 * measured from different files hash differently, and re-measuring the same
 * files reproduces the same hash.
 *
 * The draft stamp rides along on the resolved profile. Every engine entry point
 * already takes `SourceProfile & PolicyStamp`, so recovery, provenance,
 * findings and the export gate all see it without a signature change — and the
 * gate blocks the recovered artifact on it.
 */

import type { DraftStamp, RegisteredSourceProfile } from "../engine/adapter-types";
import type { SourceProfileBase, SourceProfileDelta } from "./schema";
import { canonicalProfileJson, hashPolicy, mergeProfile, type ResolvedSourceProfile } from "./resolve";

/** A resolved draft: a normal resolved profile whose stamp marks it unapproved. */
export type ResolvedDraftProfile = ResolvedSourceProfile & { draft: DraftStamp };

export function resolveDraftProfile(
  base: SourceProfileBase,
  delta: SourceProfileDelta,
  stamp: DraftStamp
): ResolvedDraftProfile {
  const merged: RegisteredSourceProfile = mergeProfile(base, delta);
  return {
    ...merged,
    // A draft has no repo profile to override, so there is never a revision.
    overrideRevision: 0,
    policyHash: hashPolicy(canonicalProfileJson(merged)),
    draft: stamp
  };
}

/** True when a resolved profile came from a local measurement, not the repo. */
export function isDraftProfile(
  profile: ResolvedSourceProfile
): profile is ResolvedDraftProfile {
  return profile.draft !== undefined;
}
